import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Save, Printer, CheckCircle2, AlertCircle, CreditCard, Building, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';
import { exportFormPdfFromDom } from '../../utils/domFormPdf';
import { fillEmpty, mergeSaved } from '../../utils/formMerge';
import SignatureCanvas from 'react-signature-canvas';

const BillingAgreementForm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const patientId = searchParams.get('patientId');
  const clientSignatureRef = useRef(null);
  const responsiblePartySignatureRef = useRef(null);
  const representativeSignatureRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    // Service Schedule
    serviceHoursPerWeek: '',
    ratePerHour: '',
    servicesProvided: '',
    serviceDays: {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false,
    },
    
    // Responsible Party
    responsiblePartyName: '',
    responsiblePartyAddress: '',
    responsiblePartyEmail: '',
    
    // Payment Method
    paymentMethod: 'check', // check, ach, credit_card
    
    // ACH Information
    achBankName: '',
    achAccountNumber: '',
    achRoutingNumber: '',
    achNameOnAccount: '',
    achBillingAddress: '',
    achCity: '',
    achState: '',
    achZip: '',
    
    // Credit Card Information
    ccType: '',
    ccExpirationDate: '',
    ccNumber: '',
    ccNameOnCard: '',
    ccBillingAddress: '',
    ccCity: '',
    ccState: '',
    ccZip: '',
    ccSecurityCode: '',
    
    // Agreement
    agreedToTerms: false,
    
    // Signatures
    signatureDate: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    if (patientId) {
      fetchPatientData();
      api.get(`/admission-forms/${patientId}/billing_agreement`).then(r => { const d = r.data?.form?.data; if (d) setFormData(prev => mergeSaved(prev, d)); }).catch(() => {});
    }
  }, [patientId]);

  const fetchPatientData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/patients/${patientId}`);
      if (response.data) {
        const patient = response.data.patient || response.data;
        setFormData(prev => fillEmpty(prev, {
          
          responsiblePartyName: patient.emergencyContactName || '',
          responsiblePartyAddress: patient.address || '',
        }));
      }
    } catch (error) {
      console.error('Error fetching patient:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('day_')) {
      const day = name.replace('day_', '');
      setFormData(prev => ({
        ...prev,
        serviceDays: { ...prev.serviceDays, [day]: checked }
      }));
    } else {
      setFormData(prev => ({ 
        ...prev, 
        [name]: type === 'checkbox' ? checked : value 
      }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Get signature data
      const signatures = {
        clientSignature: clientSignatureRef.current && !clientSignatureRef.current.isEmpty() 
          ? clientSignatureRef.current.toDataURL() : '',
        responsiblePartySignature: responsiblePartySignatureRef.current && !responsiblePartySignatureRef.current.isEmpty()
          ? responsiblePartySignatureRef.current.toDataURL() : '',
        representativeSignature: representativeSignatureRef.current && !representativeSignatureRef.current.isEmpty()
          ? representativeSignatureRef.current.toDataURL() : '',
      };

      const payload = {
        ...formData,
        ...signatures,
        patientId,
        formType: 'billing_agreement',
      };

      await api.post('/admission-forms/save', payload);
      toast.success('Billing Agreement saved successfully');
    } catch (error) {
      console.error('Error saving form:', error);
      toast.error('Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => exportFormPdfFromDom(document.getElementById('main-content'), 'Payment of Services / Billing Agreement', patientId);

  if (!patientId) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-600" />
              <p className="text-yellow-800">Please select a patient to complete this form.</p>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="billing-agreement-form">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-t-xl p-6 text-white print:bg-teal-500">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <CreditCard className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Payment of Services / Billing Agreement</h1>
                <p className="text-teal-100">IVORY HOME HEALTH CARE, INC.</p>
              </div>
            </div>
            <div className="flex gap-2 print:hidden">
              <button
                data-testid="print-form-btn"
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              >
                <Printer className="w-4 h-4" />
                Print
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-white text-teal-600 hover:bg-teal-50 rounded-lg transition-colors font-medium"
                data-testid="save-form-btn"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Form'}
              </button>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-b-xl shadow-lg p-8 space-y-8">
          {/* Service Schedule */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-teal-500" />
              Service Schedule
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hours per Week</label>
                <input
                  type="number"
                  name="serviceHoursPerWeek"
                  value={formData.serviceHoursPerWeek}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  data-testid="hours-per-week"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rate per Hour ($)</label>
                <input
                  type="number"
                  step="0.01"
                  name="ratePerHour"
                  value={formData.ratePerHour}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  data-testid="rate-per-hour"
                />
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Days of Service</label>
              <div className="flex flex-wrap gap-4">
                {Object.entries(formData.serviceDays).map(([day, checked]) => (
                  <label key={day} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      name={`day_${day}`}
                      checked={checked}
                      onChange={handleChange}
                      className="w-4 h-4 rounded border-gray-300 text-teal-500 focus:ring-teal-500"
                    />
                    <span className="text-sm capitalize">{day}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Services to be Provided</label>
              <textarea
                name="servicesProvided"
                value={formData.servicesProvided}
                onChange={handleChange}
                rows={3}
                placeholder="Describe services to be provided..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </section>

          {/* Responsible Party */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Building className="w-5 h-5 text-teal-500" />
              Responsible Party for Payment
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  name="responsiblePartyName"
                  value={formData.responsiblePartyName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  name="responsiblePartyEmail"
                  value={formData.responsiblePartyEmail}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Physical/E-mail Address for Billing</label>
                <input
                  type="text"
                  name="responsiblePartyAddress"
                  value={formData.responsiblePartyAddress}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
            </div>
          </section>

          {/* Payment Terms */}
          <section className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Terms of Payment</h3>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-teal-500 flex-shrink-0 mt-0.5" />
                Invoices are sent weekly on Fridays
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-teal-500 flex-shrink-0 mt-0.5" />
                Payment is due upon receipt of invoice
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-teal-500 flex-shrink-0 mt-0.5" />
                Accounts 30 days past due may be subject to late fees
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-teal-500 flex-shrink-0 mt-0.5" />
                Services may be suspended for non-payment after 60 days
              </li>
            </ul>
          </section>

          {/* Payment Method */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-teal-500" />
              Payment Method
            </h2>
            <div className="flex gap-4 mb-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="check"
                  checked={formData.paymentMethod === 'check'}
                  onChange={handleChange}
                  className="w-4 h-4 text-teal-500 focus:ring-teal-500"
                />
                <span>Check</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="ach"
                  checked={formData.paymentMethod === 'ach'}
                  onChange={handleChange}
                  className="w-4 h-4 text-teal-500 focus:ring-teal-500"
                />
                <span>ACH (Bank Transfer)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="credit_card"
                  checked={formData.paymentMethod === 'credit_card'}
                  onChange={handleChange}
                  className="w-4 h-4 text-teal-500 focus:ring-teal-500"
                />
                <span>Credit Card</span>
              </label>
            </div>

            {/* ACH Information */}
            {formData.paymentMethod === 'ach' && (
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-4">ACH Payment Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                    <input
                      type="text"
                      name="achBankName"
                      value={formData.achBankName}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name on Account</label>
                    <input
                      type="text"
                      name="achNameOnAccount"
                      value={formData.achNameOnAccount}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                    <input
                      type="text"
                      name="achAccountNumber"
                      value={formData.achAccountNumber}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Routing Number</label>
                    <input
                      type="text"
                      name="achRoutingNumber"
                      value={formData.achRoutingNumber}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Billing Address</label>
                    <input
                      type="text"
                      name="achBillingAddress"
                      value={formData.achBillingAddress}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      name="achCity"
                      value={formData.achCity}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                      <input
                        type="text"
                        name="achState"
                        value={formData.achState}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
                      <input
                        type="text"
                        name="achZip"
                        value={formData.achZip}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Credit Card Information */}
            {formData.paymentMethod === 'credit_card' && (
              <div className="bg-purple-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-4">Credit Card Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Card Type</label>
                    <select
                      name="ccType"
                      value={formData.ccType}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    >
                      <option value="">Select...</option>
                      <option value="visa">Visa</option>
                      <option value="mastercard">Mastercard</option>
                      <option value="amex">American Express</option>
                      <option value="discover">Discover</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Expiration Date</label>
                    <input
                      type="text"
                      name="ccExpirationDate"
                      value={formData.ccExpirationDate}
                      onChange={handleChange}
                      placeholder="MM/YY"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Card Number</label>
                    <input
                      type="text"
                      name="ccNumber"
                      value={formData.ccNumber}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Security Code (CVV)</label>
                    <input
                      type="text"
                      name="ccSecurityCode"
                      value={formData.ccSecurityCode}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name on Card</label>
                    <input
                      type="text"
                      name="ccNameOnCard"
                      value={formData.ccNameOnCard}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Billing Address</label>
                    <input
                      type="text"
                      name="ccBillingAddress"
                      value={formData.ccBillingAddress}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      name="ccCity"
                      value={formData.ccCity}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                      <input
                        type="text"
                        name="ccState"
                        value={formData.ccState}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
                      <input
                        type="text"
                        name="ccZip"
                        value={formData.ccZip}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* Agreement */}
          <section>
            <label className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
              <input
                type="checkbox"
                name="agreedToTerms"
                checked={formData.agreedToTerms}
                onChange={handleChange}
                className="w-5 h-5 rounded border-gray-300 text-teal-500 focus:ring-teal-500 mt-0.5"
                data-testid="agree-checkbox"
              />
              <span className="text-sm text-gray-700">
                I have read and understand the terms of this Billing Agreement. I agree to pay for services as outlined above and understand that services may be suspended for non-payment.
              </span>
            </label>
          </section>

          {/* Signatures */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Signatures</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Client Signature</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
                  <SignatureCanvas
                    ref={clientSignatureRef}
                    canvasProps={{
                      className: 'w-full h-24 bg-white',
                      style: { width: '100%', height: '96px' }
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => clientSignatureRef.current?.clear()}
                  className="mt-1 text-xs text-red-600 hover:text-red-700"
                >
                  Clear
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Responsible Party Signature</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
                  <SignatureCanvas
                    ref={responsiblePartySignatureRef}
                    canvasProps={{
                      className: 'w-full h-24 bg-white',
                      style: { width: '100%', height: '96px' }
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => responsiblePartySignatureRef.current?.clear()}
                  className="mt-1 text-xs text-red-600 hover:text-red-700"
                >
                  Clear
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Representative Signature</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
                  <SignatureCanvas
                    ref={representativeSignatureRef}
                    canvasProps={{
                      className: 'w-full h-24 bg-white',
                      style: { width: '100%', height: '96px' }
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => representativeSignatureRef.current?.clear()}
                  className="mt-1 text-xs text-red-600 hover:text-red-700"
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                name="signatureDate"
                value={formData.signatureDate}
                onChange={handleChange}
                className="w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </section>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BillingAgreementForm;
